import { AttachMoneyOutlined, ModeStandbyOutlined, WorkOutlineOutlined } from "@mui/icons-material";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { IVendorStatementResponse } from "src/utils/interfaces/vendor";
import {
  DELETE_PURCHASE,
  DELETE_PURCHASE_RETURN,
  DELETE_VENDOR_PAYMENT,
  EDIT_PURCHASE,
  EDIT_VENDOR_PAYMENT,
  EXPORT_PURCHASE,
  VIEW_VENDOR_LEDGER_MODE_STATEMENT,
} from "../../constants";
import { getLedgerTableColumns, getStatementTableColumns } from "../../pages/vendor/constants";
import { AccountService, AnalyticsService, FinanceService, ProjectService, PurchaseService } from "../../services";
import { derivePdfColumns, useHasPermission } from "../../utils";
import { getAccountName } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { CardLayout, DeleteModal, LedgerModeToggle, MTable } from "../v3";
import {
  getStatementTypeLabel,
  isPurchaseType,
  IVendorStatementTableRow,
  PURCHASE_FILTER_TYPES,
  VendorStatementFilter,
  VendorStatementTypes,
} from "./types";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { renameCashAccount } from "../../utils/accountUtils";

export const VendorStatement = ({ name = "Vendor", onSuccess = () => {}, reloadData = 0 }) => {
  const [purchaseAndTransaction, setPurchaseAndTransaction] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    isLedgerMode: isSavedLedgerMode,
    setLedgerMode: setIsLedgerMode,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "statement",
    defaults: { filter: VendorStatementFilter.ALL, sortModel: [{ field: "date", sort: "desc" }] },
    filterValues: Object.values(VendorStatementFilter),
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    statementId: string | null;
    purchaseId?: string | null;
    type?: VendorStatementTypes;
  }>({ isOpen: false, statementId: null });

  const { id: vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditPurchase = useHasPermission(EDIT_PURCHASE);
  const hasDeletePurchase = useHasPermission(DELETE_PURCHASE);
  const hasDeletePurchaseReturn = useHasPermission(DELETE_PURCHASE_RETURN);
  const hasEditPayment = useHasPermission(EDIT_VENDOR_PAYMENT);
  const hasDeletePayment = useHasPermission(DELETE_VENDOR_PAYMENT);
  const hasExportPurchase = useHasPermission(EXPORT_PURCHASE);
  const hasLedgerAccess = useHasPermission(VIEW_VENDOR_LEDGER_MODE_STATEMENT);
  // A remembered ledger toggle never outlives the permission.
  const isLedgerMode = hasLedgerAccess && isSavedLedgerMode;

  const fetchData = useCallback(
    async (statementFilers = {}) => {
      setLoading(true);
      try {
        const vendorAnalytics = isLedgerMode
          ? await AnalyticsService.getVendorLedger({ ...statementFilers, vendorId })
          : await AnalyticsService.getVendorAnalytics({ ...statementFilers, vendorId });

        const rows: IVendorStatementResponse[] = vendorAnalytics?.data?.rows || [];

        const projectIds = Array.from(new Set(rows.map((item) => item.projectId)));
        const accountIds = Array.from(new Set(rows.map((item) => item.fromAccountId)));

        const projectResponse =
          projectIds.length > 0
            ? await ProjectService.getProjectList({
                ids: projectIds.join(","),
                limit: projectIds.length,
                includeDeleted: true,
              })
            : null;
        const projectsMap = new Map(projectResponse?.data?.rows.map((project) => [project.id, project]) || []);

        const accountResponse =
          accountIds.length > 0
            ? await AccountService.getAccounts({
                ids: accountIds.join(","),
                limit: accountIds.length,
                includeDeleted: true,
                includeOwnerDetails: true,
              })
            : null;
        const accountsMap = new Map(accountResponse?.data?.rows.map((account) => [account.id, account]) || []);

        const formattedEntries: IVendorStatementTableRow[] = rows.map((purchase, index) => {
          const project = projectsMap.get(purchase.projectId);
          const account = accountsMap.get(purchase.fromAccountId);
          const isCashOrUser = ["CASH", "USER"].includes(account?.type);

          const accountDisplayName =
            (isCashOrUser && renameCashAccount(account, user, user.company.ownerUserId)?.name) ||
            getAccountName(account?.type, account);
          return {
            sl: index + 1 + paginationState.page * paginationState.pageSize,
            id: index,
            itemId: purchase?.id,
            date: moment(purchase?.date).format("DD MMM YYYY"),
            projectOrAccount: [VendorStatementTypes.purchase_invoice, VendorStatementTypes.purchase_return].includes(
              purchase?.type,
            )
              ? project?.name
              : accountDisplayName,
            description: purchase?.description,
            typeName: getStatementTypeLabel(purchase?.type),
            purchase: isPurchaseType(purchase?.type) ? purchase?.amount : null,
            payment: purchase?.type === VendorStatementTypes.transaction ? purchase?.amount : null,
            // gst: purchase?.gst,
            balance: purchase?.balance,
            type: purchase?.type,
            meta: purchase?.meta,
          };
        });
        setPurchaseAndTransaction(formattedEntries);
        setTotalCount(vendorAnalytics?.data?.count);
      } catch (error) {
        console.error("🚀 ~ fetchData ~ error:", error);
        toast.error(`Failed to fetch ${statementFilers?.type || "all"} history`);
      } finally {
        setLoading(false);
      }
    },
    [vendorId, isLedgerMode],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      ...(!isLedgerMode && {
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        ...(selectedFilter !== VendorStatementFilter.ALL && {
          type: selectedFilter === VendorStatementFilter.purchase ? PURCHASE_FILTER_TYPES : selectedFilter,
        }),
        search: searchText,
      }),
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, dateRange, reloadData, isLedgerMode]);

  const handleLedgerModeToggle = () => {
    setIsLedgerMode((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Convert picker values to day boundaries before API calls to prevent missed records.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[selectedFilter] || `Purchase & Payments : ${name}`;
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = purchaseAndTransaction.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [key, value === undefined || value === null ? "" : String(value)]),
        ),
      );

      const pdfDocument = (
        <Listing title={pdfTitle} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: pdfTitle,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = async (params, event) => {
    const { itemId, type, meta } = params.row;
    if (type === VendorStatementTypes.purchase_invoice && !meta?.isOldBalance) {
      navigate(`/vendor/${vendorId}/invoice/${itemId}`);
    } else if (type === VendorStatementTypes.purchase_return) {
      navigate(`/vendor/${vendorId}/return/${itemId}`);
    } else if (type === VendorStatementTypes.transaction) {
      navigate(`/vendor/${vendorId}/payment/${itemId}`);
    }
  };

  const openDeleteModal = (statementData) => {
    setDeleteModal({
      isOpen: true,
      type: statementData?.type,
      statementId: statementData?.itemId,
      purchaseId: statementData?.purchaseId,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      type: null,
      statementId: null,
      purchaseId: null,
    });
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === VendorStatementTypes.transaction) {
        await FinanceService.deleteTransaction(deleteModal.statementId);
      } else if (deleteModal.type === VendorStatementTypes.purchase_invoice) {
        await PurchaseService.deletePurchase(deleteModal.purchaseId || deleteModal.statementId);
      } else if (deleteModal.type === VendorStatementTypes.purchase_return) {
        await PurchaseService.deletePurchaseReturn(deleteModal.statementId);
      }

      const deletedEntityLabel =
        deleteModal.type === VendorStatementTypes.purchase_invoice
          ? "purchase"
          : deleteModal.type === VendorStatementTypes.purchase_return
            ? "return"
            : deleteModal.type;
      toast.success(`${deletedEntityLabel} deleted successfully`);
      onSuccess?.();
    } catch (error) {
      toast.error(`Failed to delete ${deleteModal.type}`);
      console.error("🚀 ~ deleteEntity ~ error:", error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (
    statementData: IVendorStatementTableRow,
    actionType: "edit" | "delete" | "return" | "cancelReturn",
  ) => {
    const { type, itemId } = statementData;

    switch (actionType) {
      case "edit":
        if (type === VendorStatementTypes.transaction) {
          navigate(`/transaction/edit/${itemId}?vendorId=${vendorId}`);
        } else if (type === VendorStatementTypes.purchase_invoice) {
          navigate(`/vendor/purchase/edit/${itemId}?vendorId=${vendorId}`);
        }
        break;

      case "delete":
        openDeleteModal(statementData);
        break;

      case "cancelReturn":
        openDeleteModal(statementData);
        break;

      case "return":
        navigate(`/vendor/return-purchase/${itemId}`);
        break;

      default:
        break;
    }
  };

  const columnHeaders = isLedgerMode
    ? getLedgerTableColumns(
        handleActions,
        hasEditPurchase,
        hasDeletePurchase,
        hasDeletePurchaseReturn,
        hasEditPayment,
        hasDeletePayment,
      )
    : getStatementTableColumns(
        handleActions,
        hasEditPurchase,
        hasDeletePurchase,
        hasDeletePurchaseReturn,
        hasEditPayment,
        hasDeletePayment,
        selectedFilter,
      );

  const ledgerModeToggle = hasLedgerAccess ? (
    <LedgerModeToggle isLedgerMode={isLedgerMode} onToggle={handleLedgerModeToggle} />
  ) : null;

  const filterOptions = [
    { label: "All", value: VendorStatementFilter.ALL, icon: <ModeStandbyOutlined /> },
    { label: "Purchase", value: VendorStatementFilter.purchase, icon: <WorkOutlineOutlined /> },
    { label: "Payment", value: VendorStatementFilter.transaction, icon: <AttachMoneyOutlined /> },
  ];

  const pdfTitleMap = {
    [VendorStatementFilter.ALL]: `Purchase & Payments : ${name}`,
    [VendorStatementFilter.purchase]: `Purchases : ${name}`,
    [VendorStatementFilter.transaction]: `Payments : ${name}`,
  };

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          disableFilterButton={isLedgerMode}
          // Search data
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          disableSearchBar={isLedgerMode}
          startAction={ledgerModeToggle}
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportPurchase}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={purchaseAndTransaction}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          disableSorting={isLedgerMode}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Are you sure you want to delete this ${
          deleteModal?.type === VendorStatementTypes.purchase_invoice
            ? "purchase"
            : deleteModal?.type === VendorStatementTypes.purchase_return
              ? "return"
              : "transaction"
        }?`}
      />
    </>
  );
};
